import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from './http';
import './App.css';
import ConfirmDialog from './components/ConfirmDialog';
import { useQueryApi } from './hooks/useQueryApi';
import { useQueryClient } from '@tanstack/react-query';

const STEPS_KEY = '/api/admin/workflow-steps';

export default function Settings() {
  const [tab, setTab] = useState('test');
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [systemInfo, setSystemInfo] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const queryClient = useQueryClient();

  // Workflow Steps state
  const [importingSkills, setImportingSkills] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [newStep, setNewStep] = useState({ key: '', label: '', editable_by: 'both' });
  const [editingStepId, setEditingStepId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // React Query: fetch steps only when the 'steps' tab is active; cached across navigations
  const { data: stepsData, isLoading: stepsLoading } = useQueryApi(
    tab === 'steps' ? STEPS_KEY : null
  );
  const steps = Array.isArray(stepsData) ? stepsData : [];

  const invalidateSteps = () => queryClient.invalidateQueries({ queryKey: [STEPS_KEY] });

  useEffect(() => {
    api
      .get('/api/admin/system/info')
      .then((res) => setSystemInfo(res.data))
      .catch(() => {});
    api
      .get('/api/auth/me')
      .then((res) => setCurrentUser(res.data))
      .catch(() => setCurrentUser(null));
  }, []);

  const handleAddStep = async () => {
    if (!newStep.key || !newStep.label) {
      setAlert({ type: 'danger', message: 'Key and label are required' });
      return;
    }
    try {
      await api.post('/api/admin/workflow-steps', newStep);
      setNewStep({ key: '', label: '', editable_by: 'both' });
      setAlert({ type: 'success', message: 'Step added!' });
      invalidateSteps();
    } catch (err) {
      setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to add step' });
    }
  };

  const handleDeleteStep = (id) => {
    setConfirm({
      message: 'Are you sure you want to remove this step?',
      confirmLabel: 'Remove',
      destructive: true,
      onConfirm: async () => {
        try {
          await api.delete(`/api/admin/workflow-steps/${id}`);
          setAlert({ type: 'success', message: 'Step removed!' });
          invalidateSteps();
        } catch (err) {
          setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to remove step' });
        }
      }
    });
  };

  const handleStartEdit = (step) => {
    setEditingStepId(step.id);
    setEditForm({
      label: step.label,
      editable_by: step.editable_by,
      is_testing_step: step.is_testing_step
    });
  };

  const handleSaveEdit = async (id) => {
    try {
      await api.put(`/api/admin/workflow-steps/${id}`, editForm);
      setEditingStepId(null);
      setAlert({ type: 'success', message: 'Step updated!' });
      invalidateSteps();
    } catch (err) {
      setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to update step' });
    }
  };

  const handleMove = async (index, direction) => {
    const newOrder = [...steps];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= newOrder.length) return;
    [newOrder[index], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[index]];
    try {
      await api.put('/api/admin/workflow-steps/reorder', {
        order: newOrder.map((s) => s.id)
      });
      invalidateSteps();
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to reorder steps' });
    }
  };

  // Demo controls — restricted to the 'admin' account
  const isAdminUser = currentUser?.username === 'admin';

  const demoPurge = async () => {
    setAlert(null);
    setDemoLoading(true);
    try {
      const res = await api.post('/api/admin/demo/purge');
      const output = res.data?.output;
      setAlert({
        type: 'success',
        message: output || 'Demo data purged.'
      });
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to purge demo data';
      setAlert({ type: 'danger', message: msg });
    } finally {
      setDemoLoading(false);
    }
  };

  const demoLoad = async () => {
    setAlert(null);
    setDemoLoading(true);
    try {
      const res = await api.post('/api/admin/demo/load', {});
      const output = res.data?.output;
      setAlert({
        type: 'success',
        message: output || 'Demo data loaded.'
      });
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to load demo data';
      setAlert({ type: 'danger', message: msg });
    } finally {
      setDemoLoading(false);
    }
  };

  const demoLoadFinal = async () => {
    setAlert(null);
    setDemoLoading(true);
    try {
      const res = await api.post('/api/admin/demo/load-final', {});
      const output = res.data?.output;
      setAlert({
        type: 'success',
        message: output || 'Final stages data loaded.'
      });
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to load final stages data';
      setAlert({ type: 'danger', message: msg });
    } finally {
      setDemoLoading(false);
    }
  };

  const importMasterSkills = () => {
    setConfirm({
      message: 'Import master skills list from the server?',
      confirmLabel: 'Import',
      onConfirm: async () => {
        setImportingSkills(true);
        setAlert(null);
        try {
          const res = await api.post('/api/admin/settings/import-master-skills');
          const { imported, skipped } = res.data;
          setAlert({ type: 'success', message: `Import complete: ${imported} skills added, ${skipped} skipped (already existed).` });
        } catch (err) {
          setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to import skills' });
        } finally {
          setImportingSkills(false);
        }
      }
    });
  };

  const tabBtnStyle = (active) => ({
    border: 'none',
    background: 'none',
    fontWeight: 600,
    fontSize: 18,
    padding: '8px 18px',
    borderBottom: active ? '3px solid #667eea' : 'none',
    cursor: 'pointer'
  });

  return (
    <div className="dashboard-root">
      <ConfirmDialog confirm={confirm} onCancel={() => setConfirm(null)} />
      <div
        className="header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <div>
          <h1>Settings</h1>
          <div style={{ marginTop: 18 }}>
            <Link to="/admin" className="btn" style={{ textDecoration: 'none', fontWeight: 600 }}>
              Back to Admin Hub
            </Link>
          </div>
          <div
            className="settings-tabs"
            style={{ display: 'flex', gap: 16, marginTop: 24, borderBottom: '2px solid #e3e6f0' }}
          >
            <button
              className={tab === 'test' ? 'tab-btn active' : 'tab-btn'}
              style={tabBtnStyle(tab === 'test')}
              onClick={() => setTab('test')}
            >
              Test Settings
            </button>
            <button
              className={tab === 'steps' ? 'tab-btn active' : 'tab-btn'}
              style={tabBtnStyle(tab === 'steps')}
              onClick={() => setTab('steps')}
            >
              Workflow Steps
            </button>
            <button
              className={tab === 'demo' ? 'tab-btn active' : 'tab-btn'}
              style={tabBtnStyle(tab === 'demo')}
              onClick={() => setTab('demo')}
            >
              Demo Data
            </button>
          </div>
        </div>

        {/* System Info Panel */}
        <div
          style={{
            background: '#f8f9fc',
            padding: '16px 20px',
            borderRadius: '8px',
            border: '1px solid #e3e6f0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
            minWidth: '320px',
            fontSize: '0.9rem',
            color: '#5a5c69'
          }}
        >
          <h3
            style={{
              margin: '0 0 12px 0',
              fontSize: '1rem',
              color: '#4e73df',
              borderBottom: '1px solid #e3e6f0',
              paddingBottom: '8px'
            }}
          >
            System Information
          </h3>
          {systemInfo ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>Database:</strong>
                <span
                  style={{
                    color: systemInfo.database.status === 'connected' ? '#1cc88a' : '#e74a3b',
                    fontWeight: 'bold'
                  }}
                >
                  {systemInfo.database.status.toUpperCase()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Host/Port:</span>
                <span>
                  {systemInfo.database.host}:{systemInfo.database.port}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>DB Name:</span>
                <span>
                  {systemInfo.database.name} ({systemInfo.database.adapter})
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Last Migration:</span>
                <span
                  title={systemInfo.database.latestMigration}
                  style={{
                    maxWidth: '150px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {systemInfo.database.latestMigration}
                </span>
              </div>
              <div style={{ height: '1px', background: '#e3e6f0', margin: '4px 0' }}></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>API Server:</span>
                <span>
                  v{systemInfo.server.version} ({systemInfo.server.env})
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Node Engine:</span>
                <span>{systemInfo.server.nodeVersion}</span>
              </div>
              <div style={{ height: '1px', background: '#e3e6f0', margin: '4px 0' }}></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Client App:</span>
                <span>
                  v{systemInfo.client.version} ({process.env.NODE_ENV})
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>React Engine:</span>
                <span>v{React.version}</span>
              </div>
            </div>
          ) : (
            <div style={{ fontStyle: 'italic', textAlign: 'center', padding: '10px 0' }}>
              Loading system info...
            </div>
          )}
        </div>
      </div>

      {alert && (
        <div
          style={{
            margin: '16px 0',
            padding: '12px 16px',
            borderRadius: 8,
            background:
              alert.type === 'success'
                ? '#d4edda'
                : alert.type === 'warning'
                  ? '#fff3cd'
                  : '#f8d7da',
            color:
              alert.type === 'success'
                ? '#155724'
                : alert.type === 'warning'
                  ? '#856404'
                  : '#721c24',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85em' }}>{alert.message}</span>
          <button
            onClick={() => setAlert(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2em' }}
          >
            ✕
          </button>
        </div>
      )}

      <div className="main-content" style={{ marginTop: 32 }}>
        {tab === 'test' && (
          <div>
            <h2>Test Settings</h2>
            <p>
              Configure test-related options here. (Add your settings fields/components as needed.)
            </p>

            <div style={{
              border: '1px solid #e3e6f0',
              borderRadius: 12,
              padding: 20,
              background: '#f8f9fc',
              marginBottom: 24,
              marginTop: 20
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '1.1em' }}>Master Skills Registry</h3>
              <p style={{ color: '#555', fontSize: '0.9em', marginBottom: 16 }}>
                Initialize your database with the standard JPR NFPA 1010 skill sheets automatically loaded from the server's master list. Duplicates will be safely skipped.
              </p>
              <button
                className="btn"
                onClick={importMasterSkills}
                disabled={importingSkills}
                style={{
                  background: '#667eea',
                  color: '#fff',
                  fontWeight: 600,
                  opacity: importingSkills ? 0.6 : 1,
                  cursor: importingSkills ? 'not-allowed' : 'pointer'
                }}
              >
                {importingSkills ? 'Importing...' : 'Import Master Skills'}
              </button>
            </div>
          </div>
        )}

        {tab === 'steps' && (
          <div>
            <h2>Workflow Steps</h2>
            <p style={{ color: '#666', marginBottom: 20 }}>
              Define the progression steps a company goes through at a station. Steps are executed
              in order from top to bottom. The step marked with ⚡ is the <strong>Testing</strong>{' '}
              step that triggers skill testing.
            </p>

            {stepsLoading ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>
                Loading steps...
              </div>
            ) : (
              <>
                {/* Steps List */}
                <div
                  style={{
                    border: '1px solid #e3e6f0',
                    borderRadius: 12,
                    overflow: 'hidden',
                    marginBottom: 24,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8f9fc', borderBottom: '2px solid #e3e6f0' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'center', width: 80 }}>
                          Order
                        </th>
                        <th style={{ padding: '12px 16px', textAlign: 'left' }}>Key</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left' }}>Label</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Editable By</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Flags</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center', width: 200 }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {steps.map((step, index) => (
                        <tr
                          key={step.id}
                          style={{
                            borderBottom: '1px solid #f0f0f0',
                            background: step.is_testing_step ? '#fffde7' : '#fff',
                            transition: 'background 0.15s'
                          }}
                        >
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            <div
                              style={{
                                display: 'flex',
                                gap: 4,
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}
                            >
                              <button
                                onClick={() => handleMove(index, 'up')}
                                disabled={index === 0}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: index === 0 ? 'default' : 'pointer',
                                  opacity: index === 0 ? 0.3 : 1,
                                  fontSize: 18
                                }}
                                title="Move up"
                              >
                                ▲
                              </button>
                              <span
                                style={{
                                  fontWeight: 700,
                                  color: '#667eea',
                                  fontSize: '1.1em',
                                  minWidth: 20
                                }}
                              >
                                {step.step_order}
                              </span>
                              <button
                                onClick={() => handleMove(index, 'down')}
                                disabled={index === steps.length - 1}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: index === steps.length - 1 ? 'default' : 'pointer',
                                  opacity: index === steps.length - 1 ? 0.3 : 1,
                                  fontSize: 18
                                }}
                                title="Move down"
                              >
                                ▼
                              </button>
                            </div>
                          </td>
                          <td style={{ padding: '10px 16px' }}>
                            <code
                              style={{
                                background: '#f0f0f0',
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: '0.9em'
                              }}
                            >
                              {step.key}
                            </code>
                          </td>
                          <td style={{ padding: '10px 16px' }}>
                            {editingStepId === step.id ? (
                              <input
                                value={editForm.label}
                                onChange={(e) =>
                                  setEditForm({ ...editForm, label: e.target.value })
                                }
                                style={{
                                  padding: '6px 10px',
                                  borderRadius: 6,
                                  border: '1px solid #ddd',
                                  width: '90%'
                                }}
                              />
                            ) : (
                              <span style={{ fontWeight: 600 }}>{step.label}</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            {editingStepId === step.id ? (
                              <select
                                value={editForm.editable_by}
                                onChange={(e) =>
                                  setEditForm({ ...editForm, editable_by: e.target.value })
                                }
                                style={{
                                  padding: '6px 10px',
                                  borderRadius: 6,
                                  border: '1px solid #ddd'
                                }}
                              >
                                <option value="admin">Admin</option>
                                <option value="proctor">Proctor</option>
                                <option value="both">Both</option>
                              </select>
                            ) : (
                              <span
                                style={{
                                  padding: '3px 10px',
                                  borderRadius: 12,
                                  fontSize: '0.85em',
                                  fontWeight: 600,
                                  background:
                                    step.editable_by === 'admin'
                                      ? '#e3f2fd'
                                      : step.editable_by === 'proctor'
                                        ? '#fce4ec'
                                        : '#e8f5e9',
                                  color:
                                    step.editable_by === 'admin'
                                      ? '#1565c0'
                                      : step.editable_by === 'proctor'
                                        ? '#c62828'
                                        : '#2e7d32'
                                }}
                              >
                                {step.editable_by}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            {step.is_testing_step && (
                              <span title="Testing Step" style={{ fontSize: '1.3em' }}>
                                ⚡
                              </span>
                            )}
                            {step.is_system_step && (
                              <span
                                title="System Step (cannot delete)"
                                style={{ fontSize: '1.1em', marginLeft: 4 }}
                              >
                                🔒
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                              {editingStepId === step.id ? (
                                <>
                                  <button
                                    className="btn"
                                    onClick={() => handleSaveEdit(step.id)}
                                    style={{
                                      padding: '4px 14px',
                                      fontSize: '0.85em',
                                      background: '#28a745',
                                      color: '#fff',
                                      borderRadius: 6
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="btn"
                                    onClick={() => setEditingStepId(null)}
                                    style={{
                                      padding: '4px 14px',
                                      fontSize: '0.85em',
                                      background: '#6c757d',
                                      color: '#fff',
                                      borderRadius: 6
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="btn"
                                    onClick={() => handleStartEdit(step)}
                                    style={{
                                      padding: '4px 14px',
                                      fontSize: '0.85em',
                                      background: '#667eea',
                                      color: '#fff',
                                      borderRadius: 6
                                    }}
                                  >
                                    Edit
                                  </button>
                                  {!step.is_system_step && !step.is_testing_step && (
                                    <button
                                      className="btn"
                                      onClick={() => handleDeleteStep(step.id)}
                                      style={{
                                        padding: '4px 14px',
                                        fontSize: '0.85em',
                                        background: '#dc3545',
                                        color: '#fff',
                                        borderRadius: 6
                                      }}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Add New Step */}
                <div
                  style={{
                    border: '1px solid #e3e6f0',
                    borderRadius: 12,
                    padding: 20,
                    background: '#f8f9fc'
                  }}
                >
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1em' }}>Add New Step</h3>
                  <div
                    style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}
                  >
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '0.85em',
                          fontWeight: 600,
                          marginBottom: 4,
                          color: '#555'
                        }}
                      >
                        Key
                      </label>
                      <input
                        placeholder="e.g. equipment_check"
                        value={newStep.key}
                        onChange={(e) =>
                          setNewStep({
                            ...newStep,
                            key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')
                          })
                        }
                        style={{
                          padding: '8px 12px',
                          borderRadius: 6,
                          border: '1px solid #ddd',
                          width: 180
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '0.85em',
                          fontWeight: 600,
                          marginBottom: 4,
                          color: '#555'
                        }}
                      >
                        Label
                      </label>
                      <input
                        placeholder="e.g. Equipment Check"
                        value={newStep.label}
                        onChange={(e) => setNewStep({ ...newStep, label: e.target.value })}
                        style={{
                          padding: '8px 12px',
                          borderRadius: 6,
                          border: '1px solid #ddd',
                          width: 180
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '0.85em',
                          fontWeight: 600,
                          marginBottom: 4,
                          color: '#555'
                        }}
                      >
                        Editable By
                      </label>
                      <select
                        value={newStep.editable_by}
                        onChange={(e) => setNewStep({ ...newStep, editable_by: e.target.value })}
                        style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}
                      >
                        <option value="admin">Admin</option>
                        <option value="proctor">Proctor</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    <button
                      className="btn"
                      onClick={handleAddStep}
                      style={{
                        padding: '8px 20px',
                        background: '#28a745',
                        color: '#fff',
                        fontWeight: 600,
                        borderRadius: 6
                      }}
                    >
                      + Add Step
                    </button>
                  </div>
                  <p style={{ color: '#888', fontSize: '0.85em', marginTop: 10, marginBottom: 0 }}>
                    New steps are added at the end. Use the arrow buttons to reorder.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'demo' && (
          <div>
            <h2>Demo Data</h2>

            {!isAdminUser && (
              <div
                style={{
                  background: '#fff3cd',
                  color: '#856404',
                  border: '1px solid #ffc107',
                  borderRadius: 8,
                  padding: '12px 16px',
                  marginBottom: 16,
                  fontWeight: 500
                }}
              >
                🔒 Demo data functions are restricted to the <strong>admin</strong> account. Log in
                as admin to use these controls.
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <button className="btn" onClick={demoLoad} disabled={!isAdminUser || demoLoading}
                style={{ opacity: isAdminUser && !demoLoading ? 1 : 0.5, cursor: isAdminUser && !demoLoading ? 'pointer' : 'not-allowed' }}>
                {demoLoading ? 'Loading…' : 'Load Sample Test'}
              </button>
              <button className="btn" onClick={demoLoadFinal} disabled={!isAdminUser || demoLoading}
                style={{ opacity: isAdminUser && !demoLoading ? 1 : 0.5, cursor: isAdminUser && !demoLoading ? 'pointer' : 'not-allowed' }}>
                {demoLoading ? 'Loading…' : 'Load Final Stages'}
              </button>
              <button className="btn" style={{ background: '#c0392b', opacity: isAdminUser && !demoLoading ? 1 : 0.5, cursor: isAdminUser && !demoLoading ? 'pointer' : 'not-allowed' }}
                onClick={demoPurge} disabled={!isAdminUser || demoLoading}>
                {demoLoading ? 'Working…' : 'Purge Demo Data'}
              </button>
            </div>
            <ul>
              <li>Restricted to the <strong>admin</strong> account only.</li>
              <li>
                <strong>Load Sample Test</strong> creates a new test event using your existing
                station templates and proctors (up to 9 stations). If there are not enough
                proctors, dummy accounts are created with password <code>1947</code>.
              </li>
              <li>
                <strong>Load Final Stages</strong> creates a test event with 12 companies (3
                candidates each) that have completed all but their last station — useful for
                testing graduation, makeup eligibility, and review-ready workflows. Half the
                companies have at least one skill failure.
              </li>
              <li>
                <strong>Purge</strong> wipes all test events, sessions, companies, and candidates.
                Skills and station templates are preserved.
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
